<!-- eslint-disable max-len -->
<template>
  <div
    class="card"
    style="
      background-color: rgba(255,255,255,0.95);
      width: 850px;
      height: 900px;
      margin-left: 375px;
      overflow-y: auto;
    "
  >
    <div class="card-body" style="padding: 37px;">
      <blade-spinner v-if="initialLoading" />
      <template v-else-if="!redirecting">
        <div style="display: flex; flex-direction: row; justify-content: center; margin: 20px; margin-bottom: 40px;">
          <template v-for="id of [0, 1, 2]">
            <div
              class="progress-circle"
              :style="(id <= stepIndex) ? (`background-color: ${activeEnvironment.primaryColor || 'rgb(239, 139, 95)'};`) : ('background-color: rgb(0.1, 0.1, 0.1);')"
              :key="id"
            >
              <div style="width: 200px; margin-left: -100px; text-align: center; margin-top: 10px;">{{progressDots[id]}}</div>
            </div>
            <div
              :key="id + '_line' + stepIndex"
              :style="(id < stepIndex) ? (`background-color: ${activeEnvironment.primaryColor || 'rgb(239, 139, 95)'};`) : ('background-color: rgb(0.1, 0.1, 0.1);')"
              v-if="id < 2"
              class="progress-line"
            />
          </template>
        </div>
        <div
          style="
            margin: auto;
            max-width: 48vw;
            padding-top: 20px;
            padding-bottom: 30px;
          "
        >
          <div v-if="step === 'info'">
            <catalog-info-page
              :items="linkCatalog"
              :link="link"
              :selectedItemIds="selectedItemIds"
              :onToggle="toggleItem"
              :expirationLabel="expirationLabel"
            />
            <div style="width: 100%; display: flex; flex-flow: row nowrap; justify-content: end;">
              <b-button :disabled="selectedItemIds.length === 0" @click="goToPayment">Continue</b-button>
            </div>
          </div>
          <div v-else-if="step === 'payment'">
            <catalog-payment-page
              :clientToken="clientToken"
              :price="price"
              :couponCode="couponCode"
              :coupon="coupon"
              :selectedItems="selectedItems"
              :registrationLinkId="linkId"
              :expirationLabel="paymentExpirationLabel"
              :errorMessage="errorMessage"
              @coupon-code-change="onCouponCodeChange"
              @charged="onCharged"
            />
            <div style="text-align: right; margin-top: 15px;">
              <b-button class="mr-2" variant="outline-secondary" @click="step = 'info'">Back</b-button>
            </div>
          </div>
          <div v-else-if="step === 'confirmation'">
            <h2>Purchase Complete</h2>
            <div style="font-size: 18px;">{{confirmationMessage}}</div>
            <div style="text-align: right;">
              <b-button class="mt-4" @click="goToHub">Go to Hub</b-button>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import CatalogInfoPage from './CatalogInfoPage';
import CatalogPaymentPage from './CatalogPaymentPage';

/** Accepts a populated doc or a raw id and returns the id. */
export function idOf(x) {
  if (!x) return null;
  if (x._id) return x._id;
  if (x.id) return x.id;
  if (typeof x === 'string') return x;
  return x;
}

export function formatDate(date) {
  const d = new Date(date);
  return isNaN(d.getTime()) ? null : d.toLocaleDateString();
}

export default {
  components: {
    CatalogInfoPage,
    CatalogPaymentPage
  },
  data() {
    return {
      link: null,
      linkId: null,
      step: 'info',
      selectedItemIds: [],
      couponCode: '',
      price: null,
      basePrice: null,
      coupon: null,
      clientToken: null,
      errorMessage: null,
      expirationDate: null,
      charging: false,
      initialLoading: true,
      redirecting: false,
      progressDots: [
        'Select Content',
        'Payment',
        'Confirmation'
      ]
    };
  },
  computed: {
    ...mapGetters([
      'activeEnvironment'
    ]),
    stepIndex() {
      return { info: 0, payment: 1, confirmation: 2 }[this.step];
    },
    linkCatalog() {
      return (this.link && Array.isArray(this.link.catalog)) ? this.link.catalog : [];
    },
    selectedItems() {
      return this.linkCatalog.filter(item => this.selectedItemIds.includes(idOf(item && item.gameSet)));
    },
    /** Expiration note computed from the link itself (shown before purchase). */
    expirationLabel() {
      if (!this.link || !this.link.contentExpires) return null;
      if (this.link.contentExpirationDaysAfterPurchase > 0) {
        return `Expires ${this.link.contentExpirationDaysAfterPurchase} days after purchase`;
      }
      if (this.link.contentExpirationDate) {
        const formatted = formatDate(this.link.contentExpirationDate);
        if (formatted) return `Expires on ${formatted}`;
      }
      return null;
    },
    /** Prefers the actual expirationDate returned by the successful charge. */
    paymentExpirationLabel() {
      if (this.expirationDate) {
        const formatted = formatDate(this.expirationDate);
        if (formatted) return `Expires on ${formatted}`;
      }
      return this.expirationLabel;
    },
    confirmationMessage() {
      if (this.expirationDate) {
        const formatted = formatDate(this.expirationDate);
        if (formatted) return `Your content is available now and expires on ${formatted}.`;
      }
      if (this.expirationLabel) {
        return `Your content is available now. ${this.expirationLabel}.`;
      }
      return 'Your content is available now.';
    }
  },
  watch: {
    selectedItemIds: {
      deep: true,
      handler() {
        this.loadPrice();
      }
    },
    couponCode() {
      this.loadPrice();
    }
  },
  async created() {
    const linkId = this.$route.params.registrationLinkId;
    if (!linkId) {
      this.redirecting = true;
      this.$router.replace('/hub');
      return;
    }
    this.linkId = linkId;

    // Resolve the link by value so this page is authoritative about which
    // catalog it is showing, independent of me.registrationLinkUsed.
    let link = null;
    try {
      link = await this.$store.dispatch('getRegistrationLinkByLink', linkId);
    } catch (ex) {
      console.error(ex);
      link = this.$store.state.auth.me && this.$store.state.auth.me.registrationLinkUsed;
    }
    if (!link || !Array.isArray(link.catalog) || link.catalog.length === 0) {
      this.redirecting = true;
      this.$router.replace('/hub');
      return;
    }
    this.link = link;
    this.selectedItemIds = link.catalog.map(item => idOf(item && item.gameSet)).filter(Boolean);

    let purchased = false;
    try {
      purchased = await this.$store.dispatch('hasPurchasedCatalog', {
        registrationLinkId: this.linkId
      });
    } catch (ex) {
      console.error(ex);
    }
    if (purchased) {
      this.redirecting = true;
      this.$router.replace('/hub');
      return;
    }

    await this.loadPrice();
    this.initialLoading = false;
  },
  methods: {
    toggleItem(id) {
      if (!id) return;
      const idx = this.selectedItemIds.indexOf(id);
      if (idx >= 0) {
        this.selectedItemIds.splice(idx, 1);
      } else {
        this.selectedItemIds.push(id);
      }
    },
    async loadPrice() {
      if (this.redirecting) return;
      try {
        const resp = await this.$store.dispatch('getCatalogPrice', {
          registrationLinkId: this.linkId,
          itemIds: this.selectedItemIds,
          couponCode: this.couponCode || undefined
        });
        if (!resp) return;
        if (resp.hasPaid) {
          this.goToHub();
          return;
        }
        this.price = resp.price;
        this.basePrice = resp.price;
        this.coupon = resp.coupon || null;
        this.errorMessage = null;
      } catch (ex) {
        console.error(ex);
        this.errorMessage = this.errorText(ex) || 'Failed to load pricing. Please try again.';
      }
    },
    errorText(ex) {
      if (!ex) return null;
      const data = ex.response && ex.response.data;
      if (typeof data === 'string') return data;
      if (data && typeof data === 'object') return data.message || data.error || data.data || null;
      return ex.message || null;
    },
    async goToPayment() {
      // Generate the client token once when entering the payment step.
      // (DropinContainer also generates a token internally if it needs one.)
      if (!this.clientToken) {
        this.clientToken = await this.$store.dispatch('generateClientToken');
      }
      this.errorMessage = null;
      this.step = 'payment';
    },
    onCouponCodeChange(code) {
      this.couponCode = code || '';
    },
    onCharged(result) {
      if (result && result.success) {
        this.expirationDate = result.expirationDate || null;
        this.charging = false;
        this.errorMessage = null;
        this.step = 'confirmation';
      } else {
        this.charging = false;
        this.errorMessage = (result && result.error) || 'Payment failed, please try again.';
      }
    },
    goToHub() {
      this.redirecting = true;
      this.$router.replace('/hub');
    }
  }
};
</script>

<style lang="scss" scoped>
.progress-line
{
  width: 15vw;
  height: 1px;
  margin-top: 6px;
  background-color: black;
}

.progress-circle
{
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: black;
  text-align: left;
}
</style>



// WEBPACK FOOTER //
// src/components/braintree/CatalogPurchaseFlow.vue